import { z } from 'zod';
import { FilterOpSchema } from '../fields/registry';
import { PermissionKeySchema } from '../permissions';
import { FieldRefSchema } from '../query/ast';

/* ----------------------------- statuses ----------------------------- */

export const STATUS_CATEGORIES = ['todo', 'in_progress', 'done'] as const;
export const StatusCategorySchema = z.enum(STATUS_CATEGORIES);
export type StatusCategory = z.infer<typeof StatusCategorySchema>;

export const StatusSchema = z.strictObject({
  id: z.uuid(),
  key: z.string().min(1).max(40),
  name: z.string().min(1).max(60),
  category: StatusCategorySchema,
  color: z.string().max(16).nullable().default(null),
  position: z.int().min(0),
  /** Customer-facing wording shown in the portal; falls back to `name`. */
  portalName: z.string().max(60).nullable().default(null),
});
export type Status = z.infer<typeof StatusSchema>;

/* ----------------------------- conditions ----------------------------- */

export type ConditionRule =
  | { type: 'has_permission'; permission: z.infer<typeof PermissionKeySchema> }
  | { type: 'user_is'; who: 'assignee' | 'reporter' | 'project_lead' }
  | { type: 'user_in_role'; roleId: string }
  | { type: 'user_in_group'; groupId: string }
  | { type: 'field_matches'; field: string; op: z.infer<typeof FilterOpSchema>; value?: unknown }
  | { all: ConditionRule[] }
  | { any: ConditionRule[] }
  | { not: ConditionRule };

export const ConditionRuleSchema: z.ZodType<ConditionRule> = z.lazy(() =>
  z.union([
    z.strictObject({ type: z.literal('has_permission'), permission: PermissionKeySchema }),
    z.strictObject({
      type: z.literal('user_is'),
      who: z.enum(['assignee', 'reporter', 'project_lead']),
    }),
    z.strictObject({ type: z.literal('user_in_role'), roleId: z.uuid() }),
    z.strictObject({ type: z.literal('user_in_group'), groupId: z.uuid() }),
    z.strictObject({
      type: z.literal('field_matches'),
      field: FieldRefSchema,
      op: FilterOpSchema,
      value: z.unknown().optional(),
    }),
    z.strictObject({ all: z.array(ConditionRuleSchema).max(20) }),
    z.strictObject({ any: z.array(ConditionRuleSchema).min(1).max(20) }),
    z.strictObject({ not: ConditionRuleSchema }),
  ]),
);

/** Empty `all` = always true. */
export const ALWAYS: ConditionRule = { all: [] };

/* ----------------------------- approvals (Bolt 8) ----------------------------- */

/** `approval_required.mode` (ADR-0019 §3): every approver, or just one, must decide. */
export const APPROVAL_MODES = ['all', 'any'] as const;
export const ApprovalModeSchema = z.enum(APPROVAL_MODES);
export type ApprovalMode = z.infer<typeof ApprovalModeSchema>;

/**
 * Who resolves into the approver set of one request (ADR-0019 §3, domain/workflow-rules.md):
 * a specific user, every member of a project role or a group, or the value(s) of a user /
 * multi_user field on the ticket. `packages/shared/approvals.ts` reuses this for the runtime
 * approval-resolution DTOs.
 */
export const ApproverSpecSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('user'), userId: z.uuid() }),
  z.strictObject({ kind: z.literal('role'), roleId: z.uuid() }),
  z.strictObject({ kind: z.literal('group'), groupId: z.uuid() }),
  z.strictObject({ kind: z.literal('field'), field: FieldRefSchema }),
]);
export type ApproverSpec = z.infer<typeof ApproverSpecSchema>;

/* ----------------------------- validators ----------------------------- */

export const ValidatorSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('field_required'),
    field: FieldRefSchema,
    message: z.string().max(200).optional(),
  }),
  z.strictObject({
    type: z.literal('field_matches'),
    field: FieldRefSchema,
    op: FilterOpSchema,
    value: z.unknown().optional(),
    message: z.string().max(200).optional(),
  }),
  z.strictObject({
    type: z.literal('no_open_children'),
    message: z.string().max(200).optional(),
  }),
  /**
   * Bolt 8: on first execution creates one `ticket_approval` row per resolved approver and
   * fails the transition with 409 `approval.pending`; passes once `mode` is satisfied
   * (S-07-09, ADR-0019 §3).
   */
  z.strictObject({
    type: z.literal('approval_required'),
    approvers: ApproverSpecSchema,
    mode: ApprovalModeSchema.default('all'),
    message: z.string().max(200).optional(),
  }),
]);
export type Validator = z.infer<typeof ValidatorSchema>;
/** The `approval_required` member of `Validator`, for code that only handles approvals. */
export type ApprovalRequiredValidator = Extract<Validator, { type: 'approval_required' }>;

/* ----------------------------- post-functions ----------------------------- */

export const VALUE_TOKENS = ['$now', '$current_user'] as const;

export const PostFunctionSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('set_field'),
    field: FieldRefSchema,
    value: z.unknown(),
  }),
  z.strictObject({
    type: z.literal('assign'),
    to: z.union([
      z.enum(['reporter', 'current_user', 'unassigned']),
      z.strictObject({ userId: z.uuid() }),
    ]),
  }),
  z.strictObject({
    type: z.literal('emit_event'),
    eventType: z.string().min(1).max(80),
  }),
  z.strictObject({
    type: z.literal('add_comment'),
    body: z.string().min(1).max(5000),
    visibility: z.enum(['public', 'internal']).default('internal'),
  }),
]);
export type PostFunction = z.infer<typeof PostFunctionSchema>;

/* ----------------------------- transitions & workflows ----------------------------- */

export const TransitionSchema = z.strictObject({
  id: z.uuid(),
  key: z.string().min(1).max(40),
  name: z.string().min(1).max(60),
  /** Null = global transition available from any status. */
  fromStatusIds: z.array(z.uuid()).nullable(),
  toStatusId: z.uuid(),
  conditions: ConditionRuleSchema.default(ALWAYS),
  validators: z.array(ValidatorSchema).max(20).default([]),
  postFunctions: z.array(PostFunctionSchema).max(20).default([]),
  /** Optional transition screen (form kind = transition). */
  formId: z.uuid().nullable().default(null),
  position: z.int().min(0),
});
export type Transition = z.infer<typeof TransitionSchema>;

export const WorkflowDraftSchema = z.strictObject({
  name: z.string().min(1).max(80),
  initialStatusId: z.uuid(),
  statuses: z.array(StatusSchema).min(1).max(100),
  transitions: z.array(TransitionSchema).max(300),
});
export type WorkflowDraft = z.infer<typeof WorkflowDraftSchema>;

export interface WorkflowIssue {
  code:
    | 'initial_status_missing'
    | 'unknown_status_ref'
    | 'duplicate_status_key'
    | 'duplicate_transition_key'
    | 'status_unreachable'
    | 'no_done_status';
  message: string;
  ref?: string;
}

/** Structural validation run before publishing a draft. Pure; shared by API and UI. */
export function validateWorkflowDraft(draft: WorkflowDraft): WorkflowIssue[] {
  const issues: WorkflowIssue[] = [];
  const statusIds = new Set(draft.statuses.map((s) => s.id));

  const statusKeys = new Set<string>();
  for (const s of draft.statuses) {
    if (statusKeys.has(s.key)) {
      issues.push({
        code: 'duplicate_status_key',
        message: `duplicate status key ${s.key}`,
        ref: s.id,
      });
    }
    statusKeys.add(s.key);
  }

  if (!statusIds.has(draft.initialStatusId)) {
    issues.push({
      code: 'initial_status_missing',
      message: 'initial status is not one of the workflow statuses',
      ref: draft.initialStatusId,
    });
  }

  if (!draft.statuses.some((s) => s.category === 'done')) {
    issues.push({ code: 'no_done_status', message: 'workflow needs at least one done status' });
  }

  const transitionKeys = new Set<string>();
  const adjacency = new Map<string, Set<string>>();
  for (const t of draft.transitions) {
    if (transitionKeys.has(t.key)) {
      issues.push({
        code: 'duplicate_transition_key',
        message: `duplicate transition key ${t.key}`,
        ref: t.id,
      });
    }
    transitionKeys.add(t.key);

    if (!statusIds.has(t.toStatusId)) {
      issues.push({
        code: 'unknown_status_ref',
        message: `transition ${t.key} targets unknown status`,
        ref: t.toStatusId,
      });
    }
    const froms = t.fromStatusIds ?? [...statusIds];
    for (const from of froms) {
      if (!statusIds.has(from)) {
        issues.push({
          code: 'unknown_status_ref',
          message: `transition ${t.key} starts from unknown status`,
          ref: from,
        });
        continue;
      }
      let targets = adjacency.get(from);
      if (!targets) {
        targets = new Set<string>();
        adjacency.set(from, targets);
      }
      targets.add(t.toStatusId);
    }
  }

  // Reachability from the initial status.
  const seen = new Set<string>();
  const stack = statusIds.has(draft.initialStatusId) ? [draft.initialStatusId] : [];
  while (stack.length > 0) {
    const current = stack.pop() as string;
    if (seen.has(current)) {
      continue;
    }
    seen.add(current);
    for (const next of adjacency.get(current) ?? []) {
      stack.push(next);
    }
  }
  for (const s of draft.statuses) {
    if (!seen.has(s.id) && statusIds.has(draft.initialStatusId)) {
      issues.push({
        code: 'status_unreachable',
        message: `status ${s.name} cannot be reached from the initial status`,
        ref: s.id,
      });
    }
  }

  return issues;
}

/* ----------------------------- Bolt 4: workflow DTOs ----------------------------- */

/**
 * `POST .../workflows/{id}/publish`. `statusMapping` (removed status id → target status id)
 * is required for every removed status that still has tickets (409
 * `workflow.status_mapping_required` otherwise).
 */
export const PublishWorkflowSchema = z.strictObject({
  statusMapping: z.record(z.uuid(), z.uuid()).default({}),
});
export type PublishWorkflow = z.infer<typeof PublishWorkflowSchema>;

/** Immutable published form stored in `workflow_version.snapshot` (ADR-0013 §6). */
export const WorkflowSnapshotSchema = z.strictObject({
  version: z.int().min(1),
  initialStatusId: z.uuid(),
  statuses: z.array(StatusSchema).min(1).max(100),
  transitions: z.array(TransitionSchema).max(300),
});
export type WorkflowSnapshot = z.infer<typeof WorkflowSnapshotSchema>;

export const WorkflowSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  name: z.string().min(1).max(80),
  /** Null until first published. */
  publishedVersion: z.int().min(1).nullable(),
  /** True when the draft differs from the published version. */
  draftDirty: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Workflow = z.infer<typeof WorkflowSchema>;

export const WorkflowVersionSchema = z.object({
  id: z.uuid(),
  workflowId: z.uuid(),
  version: z.int().min(1),
  snapshot: WorkflowSnapshotSchema,
  statusMapping: z.record(z.uuid(), z.uuid()).nullable(),
  publishedBy: z.uuid().nullable(),
  publishedAt: z.iso.datetime(),
});
export type WorkflowVersion = z.infer<typeof WorkflowVersionSchema>;

/** `POST /projects/{id}/workflows`; without `draft` a minimal Open → Done draft is created. */
export const CreateWorkflowSchema = z.strictObject({
  name: z.string().min(1).max(80),
  draft: WorkflowDraftSchema.optional(),
});
export type CreateWorkflow = z.infer<typeof CreateWorkflowSchema>;

/** `GET .../workflows/{id}`: the workflow with its editable draft (Bolt 4). */
export const WorkflowDetailSchema = WorkflowSchema.extend({ draft: WorkflowDraftSchema });
export type WorkflowDetail = z.infer<typeof WorkflowDetailSchema>;
